import type { Asset, SupportedCurrency, Transaction } from '../types';
import { convertCurrency } from './currency';
import { normalizeMoneyUnit } from './quoteNormalization';
import { resolveTransactionUnitPrice } from './costBasis';

export type PortfolioAnalytics = {
    assets: Asset[];
    monthlyDividends: { name: string; value: number; dividends: { symbol: string; amount: number }[] }[];
    dateRange: { start: Date; end: Date };
};

type AssetAccumulator = {
    symbol: string;
    name: string;
    quantity: number;
    totalInvested: number;
    totalDividends: number;
    lastPrice: number;
    lastNativePrice?: number;
    lastPriceDate?: Date;
    totalInvestedNative: number;
    totalDividendsNative: number;
    firstTx: Date;
    lastTx: Date;
    firstBuy?: Date;
    isin?: string;
    exchange?: string;
    dataProviderId?: string;
    instrumentId?: string;
    priceCurrency?: string;
    nativeCurrency?: string;
    dividends: NonNullable<Asset['dividends']>;
};

const toNumber = (value: unknown): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const normalizeCurrency = (value?: string | null, fallback = 'GBP') => (value || fallback).trim().toUpperCase();

const resolveBaseAmount = (tx: Transaction) => Math.abs(toNumber(tx.total ?? tx.amount));

const resolveBaseCurrency = (tx: Transaction, fallback: string) => normalizeCurrency(tx.totalCurrency || tx.currency, fallback);

const resolveNativeCurrency = (tx: Transaction) => {
    const raw = normalizeCurrency(tx.originalPriceCurrency || tx.priceCurrency || tx.currency || tx.totalCurrency, 'GBP');
    return normalizeMoneyUnit({ value: 1, rawCurrency: raw, exchange: tx.exchange, symbol: tx.symbol }).currency;
};

const resolveNativeUnitPrice = (tx: Transaction) => resolveTransactionUnitPrice(tx);

const toIsoDateOnly = (date: Date) => date.toISOString();

export const buildPortfolioAnalyticsFromTransactions = (
    transactions: Transaction[],
    selectedCurrency: SupportedCurrency | string = 'GBP'
): PortfolioAnalytics => {
    const activeTransactions = transactions.filter((tx) => !tx.deletedAt);
    const nonSyntheticTransactions = activeTransactions.filter((tx) => !tx.isSynthetic);
    const effectiveTransactions = nonSyntheticTransactions.length ? nonSyntheticTransactions : activeTransactions;

    if (!effectiveTransactions.length) {
        const now = new Date();
        return { assets: [], monthlyDividends: [], dateRange: { start: now, end: now } };
    }

    const baseCurrency = normalizeCurrency(selectedCurrency);
    const assetMap = new Map<string, AssetAccumulator>();
    const dividendsByMonth = new Map<
        string,
        { name: string; value: number; year: number; monthIndex: number; dividends: { symbol: string; amount: number }[] }
    >();

    const sortedTransactions = [...effectiveTransactions].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const minDate = new Date(sortedTransactions[0].date);
    const maxDate = new Date(sortedTransactions[sortedTransactions.length - 1].date);

    sortedTransactions.forEach((tx) => {
        const date = new Date(tx.date);
        const symbolUpper = String(tx.symbol || tx.displaySymbol || '').trim().toUpperCase();
        if (!symbolUpper || Number.isNaN(date.getTime())) return;

        if (!assetMap.has(symbolUpper)) {
            assetMap.set(symbolUpper, {
                symbol: symbolUpper,
                name: (tx.name || symbolUpper).trim(),
                quantity: 0,
                totalInvested: 0,
                totalDividends: 0,
                lastPrice: 0,
                firstTx: date,
                lastTx: date,
                totalInvestedNative: 0,
                totalDividendsNative: 0,
                isin: tx.isin,
                exchange: tx.exchange,
                dataProviderId: tx.dataProviderId,
                instrumentId: tx.instrumentId,
                priceCurrency: undefined,
                nativeCurrency: undefined,
                dividends: [],
            });
        }

        const asset = assetMap.get(symbolUpper)!;
        if (date > asset.lastTx) asset.lastTx = date;
        if (date < asset.firstTx) asset.firstTx = date;
        if (!asset.isin && tx.isin) asset.isin = tx.isin;
        if (!asset.exchange && tx.exchange) asset.exchange = tx.exchange;
        if (!asset.dataProviderId && tx.dataProviderId) asset.dataProviderId = tx.dataProviderId;
        if (!asset.instrumentId && tx.instrumentId) asset.instrumentId = tx.instrumentId;

        const quantity = Math.abs(toNumber(tx.quantity || tx.shares));
        const baseAmountRaw = resolveBaseAmount(tx);
        const txBaseCurrency = resolveBaseCurrency(tx, baseCurrency);
        const amountInBase = convertCurrency(baseAmountRaw, txBaseCurrency, baseCurrency);
        const nativeUnitPrice = resolveNativeUnitPrice(tx);
        const nativeCurrency = resolveNativeCurrency(tx);
        const nativeAmount = nativeUnitPrice > 0 && quantity > 0
            ? nativeUnitPrice * quantity
            : convertCurrency(baseAmountRaw, txBaseCurrency, nativeCurrency);
        const nativePriceInBase = nativeUnitPrice > 0 ? convertCurrency(nativeUnitPrice, nativeCurrency, baseCurrency) : 0;
        const actionLower = String(tx.action || '').trim().toLowerCase();

        if (nativeCurrency) {
            asset.priceCurrency = nativeCurrency;
            asset.nativeCurrency = nativeCurrency;
        }

        if (actionLower.includes('buy')) {
            asset.quantity += quantity;
            asset.totalInvested += amountInBase;
            asset.totalInvestedNative += nativeAmount;
            if (!asset.firstBuy || date < asset.firstBuy) asset.firstBuy = date;
            if (nativePriceInBase > 0) {
                asset.lastPrice = nativePriceInBase;
                asset.lastPriceDate = date;
            }
            if (nativeUnitPrice > 0) asset.lastNativePrice = nativeUnitPrice;
            return;
        }

        if (actionLower.includes('sell')) {
            if (asset.quantity > 0) {
                const qtySold = Math.min(quantity, asset.quantity);
                const ratio = (asset.quantity - qtySold) / asset.quantity;
                asset.quantity = Math.max(0, asset.quantity - qtySold);
                asset.totalInvested *= ratio;
                asset.totalInvestedNative *= ratio;
            }
            if (nativePriceInBase > 0) {
                asset.lastPrice = nativePriceInBase;
                asset.lastPriceDate = date;
            }
            if (nativeUnitPrice > 0) asset.lastNativePrice = nativeUnitPrice;
            return;
        }

        if (actionLower.includes('dividend') || actionLower.includes('interest')) {
            if (symbolUpper === 'CASH') return;

            asset.totalDividends += amountInBase;
            asset.totalDividendsNative += nativeAmount;

            const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
            const existingMonth = dividendsByMonth.get(monthKey);
            const name = `${date.toLocaleString('default', { month: 'short' })} ${String(date.getFullYear()).slice(-2)}`;
            const monthEntry = existingMonth || { name, value: 0, year: date.getFullYear(), monthIndex: date.getMonth(), dividends: [] };
            monthEntry.value += amountInBase;
            monthEntry.dividends.push({ symbol: symbolUpper, amount: amountInBase });
            dividendsByMonth.set(monthKey, monthEntry);

            asset.dividends.push({
                date,
                amount: amountInBase,
                quantityAtDate: quantity,
                currency: baseCurrency,
                amountNative: nativeAmount,
                nativeCurrency,
                amountPerShare: nativeUnitPrice,
                priceCurrency: nativeCurrency,
            });

            if (nativePriceInBase > 0) {
                asset.lastPrice = nativePriceInBase;
                asset.lastPriceDate = date;
            }
            if (nativeUnitPrice > 0) asset.lastNativePrice = nativeUnitPrice;
        }
    });

    const assets: Asset[] = Array.from(assetMap.values())
        .map((data) => {
            const nativeCurrency = data.nativeCurrency || data.priceCurrency || ((data.exchange || '').toUpperCase().includes('LSE') ? 'GBP' : 'USD');
            const quoteCurrency = nativeCurrency;
            return {
                id: data.symbol,
                symbol: data.symbol,
                name: data.name,
                type: (data.name.toLowerCase().includes('etf') ? 'ETF' : data.symbol === 'CASH' ? 'Cash' : 'Stock') as Asset['type'],
                sector: 'Unclassified',
                quantity: data.quantity,
                avgPrice: data.quantity > 0 ? data.totalInvested / data.quantity : 0,
                avgPriceNative: data.quantity > 0 ? data.totalInvestedNative / data.quantity : 0,
                currentPrice: data.lastPrice,
                baseCurrency,
                quoteCurrency,
                nativeCurrency,
                quoteScaleToBase: 1,
                currentPriceNative: data.lastNativePrice,
                currency: quoteCurrency,
                dividendYield: 0,
                historicDividendsReceived: data.totalDividends,
                historicDividendsReceivedNative: data.totalDividendsNative,
                importedDividendCash: data.totalDividends,
                importedDividendCashNative: data.totalDividendsNative,
                totalDividends: data.totalDividends,
                totalInvested: data.totalInvested,
                totalInvestedNative: data.totalInvestedNative,
                dividends: data.dividends,
                firstBuyDate: toIsoDateOnly(data.firstBuy ?? data.firstTx),
                priceUpdatedAt: data.lastPriceDate?.toISOString(),
                isin: data.isin,
                exchange: data.exchange,
                dataProviderId: data.dataProviderId,
                instrumentId: data.instrumentId,
                priceCurrency: quoteCurrency,
            };
        })
        .filter((asset) => asset.quantity > 0 || asset.totalDividends > 0 || asset.symbol === 'CASH');

    const monthlyDividends = Array.from(dividendsByMonth.values())
        .sort((a, b) => (a.year !== b.year ? a.year - b.year : a.monthIndex - b.monthIndex))
        .map(({ name, value, dividends }) => ({ name, value, dividends }));

    return { assets, monthlyDividends, dateRange: { start: minDate, end: maxDate } };
};
